import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { Column } from '@elementary/common-ui/components';
import * as atoms from '@elementary/common-ui/atoms';
import useDataTestsEnabled from '@elementary/common-ui/hooks/useDataTestsEnabled';
import {
  AgentCustomSqlTestConfigChange,
  AgentCustomSqlTestConfigPlan,
  AgentCustomSqlTestCreateChange,
  FieldChange,
  getAgentTestChangeRowId,
  isAgentCustomSqlTestCreateChange,
} from '@elementary/common-ui/types';
import {
  DEFAULT_CUSTOM_SQL_CLOUD_CONFIG,
  DEFAULT_CUSTOM_SQL_CLOUD_SCHEDULE,
  formatCustomSqlCloudSchedule,
  formatCustomSqlRowCountCondition,
  getCustomSqlFailRowCount,
  getCustomSqlQueryTimeoutSeconds,
  getCustomSqlSampleRowLimit,
  getCustomSqlScheduleFromChange,
  getCustomSqlStoreRowSample,
  getCustomSqlSubmitLabel,
  getCustomSqlWarnRowCount,
  resolveCustomSqlDestination,
} from '@elementary/common-ui/utils/customSqlTestConfig';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import {
  DEFAULT_CUSTOM_SQL_TEST_PATH,
  buildCustomSqlPlanWizardState,
} from 'testManagement/utils/customSqlPlanWizardMapping';
import { AgentConfigPreviewShell } from '../agentConfigPreviewShared';
import { SEVERITY_OPTIONS } from '../CodeTestAgentConfigPreview/constants';
import { BREADCRUMB_LABELS, STORAGE_KEY } from './constants';
import { CustomSqlPreviewRow, CustomSqlTestConfigFieldKey } from './types';

const extractNew = <T,>(field?: FieldChange<T>): T | undefined => field?.new;

const getTestPath = (change: AgentCustomSqlTestConfigChange): string =>
  extractNew(change.path) || DEFAULT_CUSTOM_SQL_TEST_PATH;

const isCloudRow = (
  row: CustomSqlPreviewRow,
  isSqlCloudTestsEnabled: boolean,
): boolean =>
  resolveCustomSqlDestination(row.configChange, isSqlCloudTestsEnabled) ===
  'cloud';

const getSqlPreviewField = (
  change: AgentCustomSqlTestConfigChange,
): FieldChange<string | null> | undefined => {
  const sqlQuery = change.rawSqlQuery ?? change.sqlQuery;
  if (!sqlQuery) return undefined;
  const collapse = (value: string | null) =>
    value ? value.replace(/\s+/g, ' ').trim() : value;
  return {
    current: collapse(sqlQuery.current),
    new: collapse(sqlQuery.new),
  };
};

const buildCustomSqlCreatePreviewRow = (
  change: AgentCustomSqlTestCreateChange,
  isSqlCloudTestsEnabled: boolean,
): CustomSqlPreviewRow => {
  const isCloud =
    resolveCustomSqlDestination(change, isSqlCloudTestsEnabled) === 'cloud';
  return {
    id: getAgentTestChangeRowId(change),
    asset_id: change.assetId ?? change.primaryAssetId,
    schema: '',
    table: change.resourceName ?? '',
    package: isCloud ? 'elementary' : 'dbt',
    name: change.name,
    type: 'Custom SQL',
    severity: extractNew(change.severity) || 'error',
    owners: extractNew(change.owners) ?? [],
    tags: extractNew(change.tags) ?? [],
    args: {},
    updated_at: '',
    meta: {},
    is_singular: true,
    is_custom_sql: true,
    original_path: isCloud ? '' : `${getTestPath(change)}/${change.name}.sql`,
    is_cloud: isCloud,
    source_type: isCloud ? 'cloud' : 'dbt',
    channels: [],
    model_name: change.resourceName,
    configChange: change,
    isCreateRow: true,
  };
};

const getPresentFields = (
  plan: AgentCustomSqlTestConfigPlan,
  isSqlCloudTestsEnabled: boolean,
): Set<CustomSqlTestConfigFieldKey> => {
  const fields = new Set<CustomSqlTestConfigFieldKey>();
  if (plan.operation === 'delete') return fields;

  for (const change of plan.changes) {
    const destination = resolveCustomSqlDestination(
      change,
      isSqlCloudTestsEnabled,
    );
    if (change.sqlQuery || change.rawSqlQuery) fields.add('sqlQuery');
    if (change.path || destination === 'pr') fields.add('path');
    if (change.severity) fields.add('severity');
    if (change.description) fields.add('description');
    if (change.tags) fields.add('tags');
    if (change.owners) fields.add('owners');
    if (change.qualityDimension) fields.add('qualityDimension');
    if (destination === 'cloud') {
      if (getCustomSqlFailRowCount(change) != null) fields.add('errorIf');
      if (getCustomSqlWarnRowCount(change) != null) fields.add('warnIf');
      fields.add('schedule');
      fields.add('storeRowSample');
      fields.add('sampleRowLimit');
      fields.add('queryTimeout');
    }
  }
  return fields;
};

const CustomSqlTestAgentConfigPreview = () => {
  const navigate = useNavigateWithEnv();
  const plan = useRecoilValue(atoms.agentCustomSqlTestConfigPlan);
  const isSqlCloudTestsEnabled = useDataTestsEnabled(plan?.envId);

  const handleRowClick = useCallback(
    (row: CustomSqlPreviewRow) => {
      if (!plan || plan.operation === 'delete') return;

      const change =
        plan.changes.find((item) => getAgentTestChangeRowId(item) === row.id) ??
        row.configChange;

      navigate('/test-management/plan-edit-test', {
        state: buildCustomSqlPlanWizardState(
          change,
          isSqlCloudTestsEnabled,
          plan.envId,
        ),
      });
    },
    [navigate, plan, isSqlCloudTestsEnabled],
  );

  const buildDynamicColumns = useCallback(
    ({
      presentFields,
      columnBuilders,
    }: Parameters<
      React.ComponentProps<
        typeof AgentConfigPreviewShell<
          AgentCustomSqlTestConfigChange,
          CustomSqlPreviewRow
        >
      >['buildDynamicColumns']
    >[0]) => {
      const columns: Column<CustomSqlPreviewRow>[] = [];

      if (presentFields.has('sqlQuery')) {
        columns.push(
          columnBuilders.buildTextColumn('custom_sql_query', 'SQL', (row) =>
            getSqlPreviewField(row.configChange),
          ),
        );
      }
      if (presentFields.has('path')) {
        columns.push(
          columnBuilders.buildTextColumn(
            'custom_sql_path',
            'Location',
            (row) => {
              if (isCloudRow(row, isSqlCloudTestsEnabled)) {
                return undefined;
              }
              return {
                current: row.configChange.path?.current ?? null,
                new: getTestPath(row.configChange),
              };
            },
          ),
        );
      }
      if (presentFields.has('severity')) {
        columns.push(
          columnBuilders.buildDropdownColumn(
            'custom_sql_severity',
            'Severity',
            SEVERITY_OPTIONS,
            (row) => row.configChange.severity,
          ),
        );
      }
      if (presentFields.has('errorIf')) {
        columns.push(
          columnBuilders.buildTextColumn(
            'custom_sql_error_if',
            'Error if',
            (row) => {
              const threshold = getCustomSqlFailRowCount(row.configChange);
              return threshold == null
                ? undefined
                : {
                    current: null,
                    new: formatCustomSqlRowCountCondition(threshold),
                  };
            },
          ),
        );
      }
      if (presentFields.has('warnIf')) {
        columns.push(
          columnBuilders.buildTextColumn(
            'custom_sql_warn_if',
            'Warn if',
            (row) => {
              const threshold = getCustomSqlWarnRowCount(row.configChange);
              return threshold == null
                ? undefined
                : {
                    current: null,
                    new: formatCustomSqlRowCountCondition(threshold),
                  };
            },
          ),
        );
      }
      if (presentFields.has('description')) {
        columns.push(
          columnBuilders.buildTextColumn(
            'custom_sql_description',
            'Description',
            (row) => row.configChange.description,
          ),
        );
      }
      if (presentFields.has('qualityDimension')) {
        columns.push(
          columnBuilders.buildTextColumn(
            'custom_sql_quality_dimension',
            'Quality dimension',
            (row) => row.configChange.qualityDimension,
          ),
        );
      }
      if (presentFields.has('tags')) {
        columns.push(
          columnBuilders.buildListColumn(
            'custom_sql_tags',
            'Tags',
            ['tag', 'tags'],
            (row) => row.configChange.tags,
            { isElasticLayout: true },
          ),
        );
      }
      if (presentFields.has('owners')) {
        columns.push(
          columnBuilders.buildListColumn(
            'custom_sql_owners',
            'Owners',
            ['owner', 'owners'],
            (row) => row.configChange.owners,
            { isElasticLayout: true },
          ),
        );
      }
      if (presentFields.has('schedule')) {
        columns.push(
          columnBuilders.buildTextColumn(
            'custom_sql_schedule',
            'Cadence',
            (row) => {
              if (!isCloudRow(row, isSqlCloudTestsEnabled)) {
                return undefined;
              }
              const schedule =
                getCustomSqlScheduleFromChange(row.configChange) ??
                DEFAULT_CUSTOM_SQL_CLOUD_SCHEDULE;
              return {
                current: null,
                new: formatCustomSqlCloudSchedule(schedule),
              };
            },
          ),
        );
      }
      if (presentFields.has('storeRowSample')) {
        columns.push(
          columnBuilders.buildBooleanColumn(
            'custom_sql_store_row_sample',
            'Store row sample',
            'Yes',
            'No',
            (row) => {
              if (!isCloudRow(row, isSqlCloudTestsEnabled)) {
                return undefined;
              }
              const value = getCustomSqlStoreRowSample(row.configChange);
              return {
                current: true,
                new: value ?? DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.store_row_sample,
              };
            },
          ),
        );
      }
      if (presentFields.has('sampleRowLimit')) {
        columns.push(
          columnBuilders.buildNumberColumn(
            'custom_sql_sample_row_limit',
            'Sample row limit',
            (row) => {
              if (!isCloudRow(row, isSqlCloudTestsEnabled)) {
                return undefined;
              }
              const value = getCustomSqlSampleRowLimit(row.configChange);
              return {
                current: null,
                new: value ?? DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.sample_row_limit,
              };
            },
          ),
        );
      }
      if (presentFields.has('queryTimeout')) {
        columns.push(
          columnBuilders.buildNumberColumn(
            'custom_sql_query_timeout',
            'Query timeout',
            (row) => {
              if (!isCloudRow(row, isSqlCloudTestsEnabled)) {
                return undefined;
              }
              const value = getCustomSqlQueryTimeoutSeconds(row.configChange);
              return {
                current: null,
                new:
                  value ??
                  DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.query_timeout_seconds,
              };
            },
            's',
          ),
        );
      }
      return columns;
    },
    [isSqlCloudTestsEnabled],
  );

  const presentFieldsForPlan = useCallback(
    (currentPlan: AgentCustomSqlTestConfigPlan) =>
      getPresentFields(currentPlan, isSqlCloudTestsEnabled),
    [isSqlCloudTestsEnabled],
  );

  return (
    <AgentConfigPreviewShell
      planAtom={atoms.agentCustomSqlTestConfigPlan}
      isCreateChange={isAgentCustomSqlTestCreateChange}
      buildCreatePreviewRow={(change) => {
        if (!isAgentCustomSqlTestCreateChange(change)) {
          throw new Error('Expected create change');
        }
        return buildCustomSqlCreatePreviewRow(change, isSqlCloudTestsEnabled);
      }}
      isCreateRowDataLoading={false}
      getPresentFields={presentFieldsForPlan}
      buildDynamicColumns={buildDynamicColumns}
      getSubmitLabel={(_operation, selectedCount, selectedChanges) =>
        getCustomSqlSubmitLabel(
          selectedChanges,
          selectedCount,
          isSqlCloudTestsEnabled,
        )
      }
      breadcrumbLabels={BREADCRUMB_LABELS}
      storageKey={STORAGE_KEY}
      onRowClick={handleRowClick}
      draftPr={plan?.draftPr}
    />
  );
};

export default CustomSqlTestAgentConfigPreview;
